import axios, {
  type AxiosAdapter,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { RefreshResponse } from '@/types/auth';
import { ApiError, toApiError } from './errors';
import { tokenStorage, type TokenStorage } from './tokenStorage';

/**
 * Window event dispatched when the session can no longer be refreshed.
 * AuthProvider listens for it and resets its state.
 */
export const AUTH_LOGOUT_EVENT = 'auth:logout';

/**
 * Endpoints for which a 401 must not trigger a token refresh
 */
const AUTH_ENDPOINTS = ['/auth/login', '/auth/refresh'];

/**
 * Get the API base URL from environment
 */
const getApiBaseUrl = (): string => {
  return `${import.meta.env.VITE_API_URL ?? ''}/api/v1`;
};

/**
 * Options for creating an API client
 */
export interface ApiClientOptions {
  baseURL: string;
  tokens: TokenStorage;
  /**
   * Called once when a token refresh fails (session is over)
   */
  onAuthFailure?: () => void;
  /**
   * Custom axios adapter (tests use a stub adapter instead of the network)
   */
  adapter?: AxiosAdapter;
  timeout?: number;
}

/**
 * API client: an axios instance with auth + error-normalisation interceptors
 */
export interface ApiClient {
  http: AxiosInstance;
  /**
   * Refresh the access token. Concurrent callers share one in-flight request.
   */
  refreshAccessToken(): Promise<string>;
  /**
   * Drop any in-flight refresh state (used by tests)
   */
  reset(): void;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

function isAuthEndpoint(url: string | undefined): boolean {
  return !!url && AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

/**
 * Create an API client.
 *
 * All refresh state lives in this closure (not at module level), so every
 * client - and every test - starts from a clean slate.
 *
 * Behaviour:
 * - adds `Authorization: Bearer <access token>` to every request
 * - on 401 (except auth endpoints) refreshes the token once and retries the request;
 *   concurrent 401s wait for the same refresh
 * - every rejection is normalised to an ApiError carrying the server message
 */
export function createApiClient({
  baseURL,
  tokens,
  onAuthFailure,
  adapter,
  timeout = 30000,
}: ApiClientOptions): ApiClient {
  const config = {
    baseURL,
    headers: { 'Content-Type': 'application/json' },
    timeout,
    ...(adapter ? { adapter } : {}),
  };

  const http = axios.create(config);
  // Separate instance without interceptors to avoid refresh loops
  const refreshHttp = axios.create(config);

  let refreshInFlight: Promise<string> | null = null;

  const doRefresh = async (): Promise<string> => {
    try {
      const refreshToken = tokens.getRefreshToken();
      if (!refreshToken) {
        throw new ApiError('No refresh token available', { status: 401 });
      }

      const response = await refreshHttp.post<RefreshResponse>('/auth/refresh', {
        refresh_token: refreshToken,
      });
      const { access_token, expires_in } = response.data;
      tokens.updateAccessToken(access_token, expires_in);
      return access_token;
    } catch (error) {
      // Session is over - clear tokens and notify the app once
      tokens.clearTokens();
      onAuthFailure?.();
      throw toApiError(error);
    }
  };

  const refreshAccessToken = (): Promise<string> => {
    if (!refreshInFlight) {
      refreshInFlight = doRefresh().finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  };

  http.interceptors.request.use((request: InternalAxiosRequestConfig) => {
    const accessToken = tokens.getAccessToken();
    if (accessToken) {
      request.headers.Authorization = `Bearer ${accessToken}`;
    }
    return request;
  });

  http.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (!axios.isAxiosError(error)) {
        return Promise.reject(toApiError(error));
      }

      const originalRequest = error.config as RetriableConfig | undefined;

      if (!originalRequest || originalRequest._retry || error.response?.status !== 401) {
        return Promise.reject(toApiError(error));
      }

      if (isAuthEndpoint(originalRequest.url)) {
        tokens.clearTokens();
        return Promise.reject(toApiError(error));
      }

      originalRequest._retry = true;

      let accessToken: string;
      const currentToken = tokens.getAccessToken();
      const sentAuthorization = originalRequest.headers.Authorization;

      if (currentToken && sentAuthorization && sentAuthorization !== `Bearer ${currentToken}`) {
        // The token was already refreshed while this request was in flight
        accessToken = currentToken;
      } else {
        try {
          accessToken = await refreshAccessToken();
        } catch (refreshError) {
          return Promise.reject(toApiError(refreshError));
        }
      }

      originalRequest.headers.Authorization = `Bearer ${accessToken}`;
      return http(originalRequest);
    }
  );

  return {
    http,
    refreshAccessToken,
    reset() {
      refreshInFlight = null;
    },
  };
}

/**
 * Default application client
 */
const defaultClient = createApiClient({
  baseURL: getApiBaseUrl(),
  tokens: tokenStorage,
  onAuthFailure: () => window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT)),
});

export const apiClient = defaultClient.http;
export const refreshAccessToken = defaultClient.refreshAccessToken;
export { tokenStorage };
